import {
  LoggerAdapter,
  InternalLogger,
  AcceptableTags,
  ErrorLevel,
} from './types';
import {
  removeSensibleDataFromError,
  sanitizeSensitiveDataInPlace,
} from 'utils/removeSensibleDataFromError';

export class Logger implements LoggerAdapter {
  constructor(
    private readonly sentryAdapter: InternalLogger,
    private readonly elasticAdapter: InternalLogger,
    private readonly simpleLoggerAdapter: InternalLogger
  ) {}

  init(): void {
    // sentry initializes automatically and doesn't need to be called
    if (process.env.NODE_ENV !== 'development') {
      this.elasticAdapter.init();
    }
  }

  fatal(
    error: string | Error,
    additionalTags?: Record<string, AcceptableTags>,
    additionalInfo?: Record<string, unknown>
  ): void {
    const sanitizedError = removeSensibleDataFromError(error);
    const sanitizedAdditionalTags =
      sanitizeSensitiveDataInPlace(additionalTags);
    const sanitizedAdditionalInfo =
      sanitizeSensitiveDataInPlace(additionalInfo);

    this.elasticAdapter.captureError(
      ErrorLevel.FATAL,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
    this.sentryAdapter.captureError(
      ErrorLevel.FATAL,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
    this.simpleLoggerAdapter.captureError(
      ErrorLevel.FATAL,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
  }

  error(
    error: string | Error,
    additionalTags?: Record<string, AcceptableTags>,
    additionalInfo?: Record<string, unknown>
  ): void {
    const sanitizedError = removeSensibleDataFromError(error);
    const sanitizedAdditionalTags =
      sanitizeSensitiveDataInPlace(additionalTags);
    const sanitizedAdditionalInfo =
      sanitizeSensitiveDataInPlace(additionalInfo);

    this.elasticAdapter.captureError(
      ErrorLevel.ERROR,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
    this.sentryAdapter.captureError(
      ErrorLevel.ERROR,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
    this.simpleLoggerAdapter.captureError(
      ErrorLevel.ERROR,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
  }

  warning(
    error: string | Error,
    additionalTags?: Record<string, AcceptableTags>,
    additionalInfo?: Record<string, unknown>
  ): void {
    const sanitizedError = removeSensibleDataFromError(error);
    const sanitizedAdditionalTags =
      sanitizeSensitiveDataInPlace(additionalTags);
    const sanitizedAdditionalInfo =
      sanitizeSensitiveDataInPlace(additionalInfo);

    this.elasticAdapter.captureError(
      ErrorLevel.WARNING,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
    this.simpleLoggerAdapter.captureError(
      ErrorLevel.WARNING,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
  }

  info(
    error: string | Error,
    additionalTags?: Record<string, AcceptableTags>,
    additionalInfo?: Record<string, unknown>
  ): void {
    const sanitizedError = removeSensibleDataFromError(error);
    const sanitizedAdditionalTags =
      sanitizeSensitiveDataInPlace(additionalTags);
    const sanitizedAdditionalInfo =
      sanitizeSensitiveDataInPlace(additionalInfo);

    this.simpleLoggerAdapter.captureError(
      ErrorLevel.INFO,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
  }

  debug(
    error: string | Error,
    additionalTags?: Record<string, AcceptableTags>,
    additionalInfo?: Record<string, unknown>
  ): void {
    const sanitizedError = removeSensibleDataFromError(error);
    const sanitizedAdditionalTags =
      sanitizeSensitiveDataInPlace(additionalTags);
    const sanitizedAdditionalInfo =
      sanitizeSensitiveDataInPlace(additionalInfo);

    this.simpleLoggerAdapter.captureError(
      ErrorLevel.DEBUG,
      sanitizedError,
      sanitizedAdditionalTags,
      sanitizedAdditionalInfo
    );
  }
}
